/**
 * FRODO Statistics Test Component
 * 
 * Allows users to test FRODO configurations on trip layers without running a full simulation.
 * Users can select a layer, configure multiple test scenarios, and view comprehensive results.
 */

import React, { useState, useMemo, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { validate as validateUuid } from 'uuid';
import {
  Box,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';

import { AppContext } from '../../context/context';
import type { Layer, Line as MapLine, Point, Polygon } from '../../context/context';
import { useFixedRoutes } from '../../context/FixedRoutesContext';
import type { FrodoConfig, FrodoReport, FrodoOffloadedRide } from '../../types/frodo';
import type { RideSchema } from '../../types/simulation';
import type { FixedRoute } from '../../types/fixedRoutes';
import {
  processFrodoOptimization,
  generateFrodoReport,
  findCandidateStops,
  isRideEligibleForFixedRoute,
} from '../../services/frodo/frodoService';
import { SpatialIndex } from '../../utils/frodo/spatialIndex';
import FrodoReportComponent from '../simulation/FrodoReport';
import FrodoRouteSelectionModal from '../simulation/FrodoRouteSelectionModal';
import { validateFrodoCustomTestInput } from './frodoTestValidation';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';

import { Icon } from '@liftango/ui-icons';
interface TestConfiguration {
  id: string;
  name: string;
  maxDistanceToStop: number;
  maxWaitTimeMinutes: number;
  excludeWheelchairUsers: boolean;
  allowTransfers: boolean;
  selectedRouteIds: string[];
  isPreset: boolean;
}

interface TestResult {
  testId: string;
  testName: string;
  report: FrodoReport;
  offloadedRides: FrodoOffloadedRide[];
  remainingRides: RideSchema[];
  processingTime: number;
  selectedRouteIds: string[];
  maxDistanceToStop: number;
  rejectionBreakdown: {
    excludedWheelchair: number;
    noOriginStops: number;
    noDestStops: number;
    noStopsAtEitherEnd: number;
    noEligibleRouteOrTime: number;
  };
}

const HOVER_PREVIEW_LAYER_NAME = '__FRODO Hover Preview__';
const HOVER_BUFFER_COLOUR = '#FF9800';
const HOVER_TRIP_COLOUR = '#00C853';
const HOVER_ROUTE_COLOUR = '#6A1B9A';

function createBufferPolygon(
  center: [number, number],
  radiusMeters: number,
  segments = 32
): [number, number][][] {
  const [lat, lng] = center;
  const latRadius = radiusMeters / 111000;
  const lngDenominator = 111000 * Math.cos((lat * Math.PI) / 180);
  const lngRadius = lngDenominator !== 0 ? radiusMeters / lngDenominator : 0;
  const ring: [number, number][] = [];

  for (let i = 0; i <= segments; i++) {
    const theta = (2 * Math.PI * i) / segments;
    ring.push([
      lat + latRadius * Math.sin(theta),
      lng + lngRadius * Math.cos(theta),
    ]);
  }

  return [ring];
}

/**
 * Convert Line object to RideSchema format
 */
function mapLineToRideSchema(line: any): RideSchema | null {
  const coords = Array.isArray(line.coordinates) ? line.coordinates : [];
  const from = coords[0] || [null, null];
  const to = coords[1] || [null, null];

  // Validate coordinates
  if (
    !Number.isFinite(from[0]) ||
    !Number.isFinite(from[1]) ||
    !Number.isFinite(to[0]) ||
    !Number.isFinite(to[1])
  ) {
    return null;
  }

  // Passengers array
  const passengers: any[] = [];
  if (line.standard_seat || line.numPassengers) {
    passengers.push({ 
      type: 'standard', 
      number_of_passengers: Number(line.standard_seat ?? line.numPassengers ?? 1) 
    });
  }
  if (line.wheelchair) {
    passengers.push({ 
      type: 'wheelchair', 
      number_of_passengers: Number(line.wheelchair) 
    });
  }
  if (passengers.length === 0) {
    passengers.push({ type: 'standard', number_of_passengers: 1 });
  }

  const rideId = line.ride_id && validateUuid(line.ride_id) ? line.ride_id : undefined;
  const userId = line.user_id && validateUuid(line.user_id) ? line.user_id : undefined;

  const ride: RideSchema = {
    external_reference_id: rideId,
    external_user_reference_id: userId,
    from_latitude: from[0],
    from_longitude: from[1],
    from_address: line.from_address || null,
    to_latitude: to[0],
    to_longitude: to[1],
    to_address: line.to_address || null,
    request_type: line.requestType || line.request_type || null,
    passengers,
    request_time: line.request_time || line.time || null,
    created_at: line.created_at || null,
    batch_id: line.batch_id || null,
    priority: line.priority || null,
  };

  if (rideId) ride.ride_id = rideId;
  if (userId) ride.user_id = userId;

  return ride;
}

/**
 * Helper function to bin rides by hour (timezone-agnostic)
 */
function binRidesPerHour(rides: RideSchema[]): { hour: string; count: number }[] {
  const bins: Record<string, number> = {};
  
  rides.forEach((ride) => {
    const timeStr = ride.request_time || ride.created_at;
    if (timeStr && typeof timeStr === 'string') {
      // Extract date and hour directly from string (timezone-agnostic)
      const cleanTime = timeStr.replace(/[Z+-]\d{2}:\d{2}$|[Z]$/, '');
      const match = cleanTime.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):/);
      if (match) {
        const date = match[1];
        const hour = match[2];
        const hourKey = `${date} ${hour}:00`;
        bins[hourKey] = (bins[hourKey] || 0) + 1;
      }
    }
  });
  
  return Object.entries(bins)
    .map(([hour, count]) => ({ hour, count }))
    .sort((a, b) => a.hour.localeCompare(b.hour));
}

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

export function FrodoStatisticsTest() {
  const { t } = useTranslation('fixedRoutes');
  const context = useContext(AppContext);
  const { routes, stops } = useFixedRoutes();
  const setLayers = context?.global?.setLayers;

  // State for layer selection
  const [selectedLayerId, setSelectedLayerId] = useState<number | null>(null);

  // State for test configurations
  const [testConfigurations, setTestConfigurations] = useState<TestConfiguration[]>(() => {
    // Initialize with three presets
    const preset1: TestConfiguration = {
      id: 'preset-1',
      name: '100m / 5min',
      maxDistanceToStop: 100,
      maxWaitTimeMinutes: 5,
      excludeWheelchairUsers: true,
      allowTransfers: false,
      selectedRouteIds: [], // Will be populated with all routes
      isPreset: true,
    };
    const preset2: TestConfiguration = {
      id: 'preset-2',
      name: '250m / 10min',
      maxDistanceToStop: 250,
      maxWaitTimeMinutes: 10,
      excludeWheelchairUsers: true,
      allowTransfers: false,
      selectedRouteIds: [],
      isPreset: true,
    };
    const preset3: TestConfiguration = {
      id: 'preset-3',
      name: '400m / 15min',
      maxDistanceToStop: 400,
      maxWaitTimeMinutes: 15,
      excludeWheelchairUsers: true,
      allowTransfers: false,
      selectedRouteIds: [],
      isPreset: true,
    };
    return [preset1, preset2, preset3];
  });

  // State for custom test dialog
  const [customTestDialogOpen, setCustomTestDialogOpen] = useState(false);
  const [newTestName, setNewTestName] = useState('');
  const [newTestMaxDistance, setNewTestMaxDistance] = useState(400);
  const [newTestMaxWaitTime, setNewTestMaxWaitTime] = useState(15);
  const [newTestExcludeWheelchair, setNewTestExcludeWheelchair] = useState(true);
  const [newTestAllowTransfers, setNewTestAllowTransfers] = useState(false);
  const [customTestFieldErrors, setCustomTestFieldErrors] = useState<{
    name?: string;
    maxDistance?: string;
    maxWait?: string;
  }>({});

  // State for route selection modal
  const [routeSelectionModalOpen, setRouteSelectionModalOpen] = useState(false);
  const [routeSelectionTestId, setRouteSelectionTestId] = useState<string | null>(null);

  // State for test execution
  const [isRunning, setIsRunning] = useState(false);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hoveredResultId, setHoveredResultId] = useState<string | null>(null);
  const [pinnedResultId, setPinnedResultId] = useState<string | null>(null);

  // Get available layers with trip data
  const availableLayers = useMemo(() => {
    if (!context?.global?.mapLayers) return [];

    return context.global.mapLayers
      .filter((layer) => {
        // Filter layers that contain Line objects with valid coordinates
        const hasValidLines = layer.shapes.some((shape) => {
          if (shape.type !== 'line') return false;
          const coords = shape.coordinates;
          return (
            Array.isArray(coords) &&
            coords.length >= 2 &&
            Array.isArray(coords[0]) &&
            Array.isArray(coords[1]) &&
            coords[0].length === 2 &&
            coords[1].length === 2 &&
            typeof coords[0][0] === 'number' &&
            typeof coords[0][1] === 'number' &&
            typeof coords[1][0] === 'number' &&
            typeof coords[1][1] === 'number'
          );
        });
        return hasValidLines;
      })
      .map((layer) => {
        const tripCount = layer.shapes.filter((s) => s.type === 'line').length;
        return {
          id: layer.layerId,
          name: layer.layerName,
          tripCount,
        };
      });
  }, [context?.global?.mapLayers]);

  // Update preset route selections when routes change
  React.useEffect(() => {
    if (routes.length > 0) {
      const allRouteIds = routes.map((r) => r.id);
      setTestConfigurations((prev) =>
        prev.map((config) => {
          if (config.isPreset && config.selectedRouteIds.length === 0) {
            return { ...config, selectedRouteIds: allRouteIds };
          }
          return config;
        })
      );
    }
  }, [routes]);

  // Get selected layer's trip data
  const selectedLayerTrips = useMemo(() => {
    if (!selectedLayerId || !context?.global?.mapLayers) return [];

    const layer = context.global.mapLayers.find((l) => l.layerId === selectedLayerId);
    if (!layer) return [];

    return layer.shapes.filter((shape) => shape.type === 'line') as any[];
  }, [selectedLayerId, context?.global?.mapLayers]);

  const hoveredResult = useMemo(
    () => testResults.find((r) => r.testId === hoveredResultId) || null,
    [testResults, hoveredResultId]
  );
  const pinnedResult = useMemo(
    () => testResults.find((r) => r.testId === pinnedResultId) || null,
    [testResults, pinnedResultId]
  );
  const previewResult = pinnedResult || hoveredResult;

  useEffect(() => {
    if (!setLayers) return;

    setLayers((prevLayers) => {
      const layersWithoutPreview = prevLayers.filter((l) => l.layerName !== HOVER_PREVIEW_LAYER_NAME);
      if (!previewResult) {
        return layersWithoutPreview;
      }

      const selectedRoutes = routes.filter((r) => previewResult.selectedRouteIds.includes(r.id));
      const selectedStopIdSet = new Set<string>();
      selectedRoutes.forEach((route) => {
        route.stops.forEach((stop) => selectedStopIdSet.add(stop.stopId));
      });
      const selectedStops = stops.filter((s) => selectedStopIdSet.has(s.id));
      const stopLookup = new Map(stops.map((s) => [s.id, s]));

      const previewShapes: Array<Point | MapLine | Polygon> = [];

      selectedStops.forEach((stop, idx) => {
        previewShapes.push({
          id: `frodo-hover-stop-${previewResult.testId}-${idx}`,
          type: 'point',
          name: `FRODO Stop ${idx + 1}`,
          description: 'FRODO stop used in hover preview',
          colour: HOVER_BUFFER_COLOUR,
          icon: 'dot',
          size: 10,
          isLocked: true,
          coordinates: [stop.coordinates.lat, stop.coordinates.lng],
        });

        previewShapes.push({
          id: `frodo-hover-buffer-${previewResult.testId}-${idx}`,
          type: 'polygon',
          name: `FRODO Buffer ${idx + 1}`,
          description: `${previewResult.maxDistanceToStop}m buffer around stop`,
          colour: HOVER_BUFFER_COLOUR,
          opacity: '0.12',
          borderColour: HOVER_BUFFER_COLOUR,
          borderWidth: 2,
          isLocked: true,
          coordinates: createBufferPolygon(
            [stop.coordinates.lat, stop.coordinates.lng],
            previewResult.maxDistanceToStop
          ),
        });
      });

      selectedRoutes.forEach((route, idx) => {
        let coordinates: [number, number][] = [];
        if (route.geometry?.coordinates && route.geometry.coordinates.length > 1) {
          coordinates = route.geometry.coordinates as [number, number][];
        } else {
          coordinates = route.stops
            .map((stopConfig) => stopLookup.get(stopConfig.stopId))
            .filter((s): s is typeof stops[number] => !!s)
            .map((s) => [s.coordinates.lat, s.coordinates.lng] as [number, number]);
        }

        if (coordinates.length > 1) {
          previewShapes.push({
            id: `frodo-hover-route-${previewResult.testId}-${idx}`,
            type: 'line',
            name: route.name || `FRODO Route ${idx + 1}`,
            colour: HOVER_ROUTE_COLOUR,
            description: 'Route preview for hovered FRODO test result',
            opacity: '0.9',
            borderColour: HOVER_ROUTE_COLOUR,
            borderWidth: 4,
            isLocked: true,
            coordinates,
            dashPattern: '8, 4',
          });
        }
      });

      // Cap highlighted trips for map performance in large datasets.
      previewResult.offloadedRides.slice(0, 400).forEach((ride, idx) => {
        if (
          Number.isFinite(ride.from_latitude) &&
          Number.isFinite(ride.from_longitude) &&
          Number.isFinite(ride.to_latitude) &&
          Number.isFinite(ride.to_longitude)
        ) {
          previewShapes.push({
            id: `frodo-hover-offloaded-${previewResult.testId}-${idx}`,
            type: 'line',
            name: `Offloaded Ride ${idx + 1}`,
            colour: HOVER_TRIP_COLOUR,
            description: 'Ride offloaded by hovered FRODO test result',
            opacity: '1',
            borderColour: HOVER_TRIP_COLOUR,
            borderWidth: 3,
            isLocked: true,
            coordinates: [
              [ride.from_latitude, ride.from_longitude],
              [ride.to_latitude, ride.to_longitude],
            ],
          });
        }
      });

      const nextLayerId =
        layersWithoutPreview.length > 0
          ? Math.max(...layersWithoutPreview.map((l) => l.layerId)) + 1
          : 0;

      const hoverLayer: Layer = {
        layerId: nextLayerId,
        layerName: HOVER_PREVIEW_LAYER_NAME,
        hidden: false,
        expanded: true,
        shapes: previewShapes,
      };

      return [...layersWithoutPreview, hoverLayer];
    });
  }, [setLayers, previewResult, routes, stops]);

  useEffect(() => {
    return () => {
      if (!setLayers) return;
      setLayers((prevLayers) => prevLayers.filter((l) => l.layerName !== HOVER_PREVIEW_LAYER_NAME));
    };
  }, [setLayers]);

  // Handle adding custom test
  const handleAddCustomTest = () => {
    const nextErrors = validateFrodoCustomTestInput({
      name: newTestName,
      maxDistanceToStop: newTestMaxDistance,
      maxWaitTimeMinutes: newTestMaxWaitTime,
    });

    if (Object.keys(nextErrors).length > 0) {
      setCustomTestFieldErrors(nextErrors);
      setError(t('frodoTest.errors.fixHighlighted'));
      return;
    }

    const allRouteIds = routes.map((r) => r.id);
    const newTest: TestConfiguration = {
      id: `custom-${Date.now()}`,
      name: newTestName.trim(),
      maxDistanceToStop: newTestMaxDistance,
      maxWaitTimeMinutes: newTestMaxWaitTime,
      excludeWheelchairUsers: newTestExcludeWheelchair,
      allowTransfers: newTestAllowTransfers,
      selectedRouteIds: allRouteIds,
      isPreset: false,
    };

    setTestConfigurations((prev) => [...prev, newTest]);
    setNewTestName('');
    setNewTestMaxDistance(400);
    setNewTestMaxWaitTime(15);
    setNewTestExcludeWheelchair(true);
    setNewTestAllowTransfers(false);
    setCustomTestDialogOpen(false);
    setCustomTestFieldErrors({});
    setError(null);
  };

  // Handle removing custom test
  const handleRemoveTest = (testId: string) => {
    setTestConfigurations((prev) => prev.filter((config) => config.id !== testId));
  };

  // Handle opening route selection modal
  const handleOpenRouteSelection = (testId: string) => {
    setRouteSelectionTestId(testId);
    setRouteSelectionModalOpen(true);
  };

  // Handle route selection change
  const handleRouteSelectionChange = (selectedRouteIds: string[]) => {
    if (routeSelectionTestId) {
      setTestConfigurations((prev) =>
        prev.map((config) =>
          config.id === routeSelectionTestId ? { ...config, selectedRouteIds } : config
        )
      );
    }
    setRouteSelectionModalOpen(false);
    setRouteSelectionTestId(null);
  };

  // Handle export comparison table as CSV
  const handleExportComparisonCSV = () => {
    if (testResults.length === 0) return;

    const headers = [
      'Test Name',
      'Total Rides',
      'Rides Offloaded',
      'Rides Remaining',
      'Offload Percentage',
      'Avg Wait Time (min)',
      'Avg Origin Distance (m)',
      'Avg Dest Distance (m)',
    ];

    const rows = testResults.map((result) => {
      const avgWaitTime =
        result.report.routeStats.length > 0
          ? (
              result.report.routeStats.reduce((sum, stat) => sum + stat.avgWaitTime, 0) /
              result.report.routeStats.length
            ).toFixed(1)
          : 'N/A';
      const avgOriginDist =
        result.report.routeStats.length > 0
          ? Math.round(
              result.report.routeStats.reduce((sum, stat) => sum + stat.avgOriginDistance, 0) /
                result.report.routeStats.length
            )
          : 'N/A';
      const avgDestDist =
        result.report.routeStats.length > 0
          ? Math.round(
              result.report.routeStats.reduce((sum, stat) => sum + stat.avgDestDistance, 0) /
                result.report.routeStats.length
            )
          : 'N/A';

      return [
        result.testName,
        (result.report.totalOffloaded + result.report.totalRemaining).toString(),
        result.report.totalOffloaded.toString(),
        result.report.totalRemaining.toString(),
        result.report.offloadPercentage.toFixed(1),
        avgWaitTime,
        avgOriginDist.toString(),
        avgDestDist.toString(),
      ];
    });

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frodo-test-comparison-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Handle running tests
  const handleRunTests = async () => {
    if (!selectedLayerId) {
      setError(t('frodoTest.errors.selectLayer'));
      return;
    }

    if (testConfigurations.length === 0) {
      setError(t('frodoTest.errors.addAtLeastOne'));
      return;
    }

    setIsRunning(true);
    setError(null);
    setTestResults([]);

    try {
      // Convert Line objects to RideSchema
      const rides: RideSchema[] = [];
      for (const line of selectedLayerTrips) {
        const ride = mapLineToRideSchema(line);
        if (ride) {
          rides.push(ride);
        }
      }

      if (rides.length === 0) {
        throw new Error(t('frodoTest.errors.noValidTrips'));
      }

      // Process each test configuration
      const results: TestResult[] = [];

      for (const config of testConfigurations) {
        const startTime = Date.now();

        // Get selected routes
        const selectedRoutes = routes.filter((r) => config.selectedRouteIds.includes(r.id));

        if (selectedRoutes.length === 0) {
          console.warn(t('frodoTest.errors.noRoutesSelected', { name: config.name }));
          continue;
        }

        // Create FRODO config
        const frodoConfig: FrodoConfig = {
          enabled: true,
          selectedRouteIds: config.selectedRouteIds,
          maxDistanceToStop: config.maxDistanceToStop,
          excludeWheelchairUsers: config.excludeWheelchairUsers,
          maxWaitTimeMinutes: config.maxWaitTimeMinutes,
          allowTransfers: config.allowTransfers,
        };

        // Process FRODO optimization
        const { offloadedRides, remainingRides } = processFrodoOptimization(
          rides,
          selectedRoutes,
          stops,
          frodoConfig
        );

        // Generate FRODO report
        const stopsMap = new Map(stops.map((s) => [s.id, s]));
        const report = generateFrodoReport(offloadedRides, rides.length, stopsMap);

        // Rejection diagnostics for faster debugging of low-offload scenarios
        const selectedStopIds = new Set<string>();
        selectedRoutes.forEach((route) => {
          route.stops.forEach((stop) => selectedStopIds.add(stop.stopId));
        });
        const filteredStops = stops.filter((stop) => selectedStopIds.has(stop.id));
        const filteredStopsMap = new Map(filteredStops.map((s) => [s.id, s]));
        const spatialIndex = new SpatialIndex();
        spatialIndex.buildIndex(filteredStops);

        const rejectionBreakdown = {
          excludedWheelchair: 0,
          noOriginStops: 0,
          noDestStops: 0,
          noStopsAtEitherEnd: 0,
          noEligibleRouteOrTime: 0,
        };

        for (const ride of rides) {
          if (config.excludeWheelchairUsers) {
            const hasWheelchair = ride.passengers?.some(
              (p) => p.type?.toLowerCase().includes('wheelchair') || p.type?.toLowerCase().includes('wchair')
            );
            if (hasWheelchair) {
              rejectionBreakdown.excludedWheelchair++;
              continue;
            }
          }

          const { originStops, destStops } = findCandidateStops(
            ride,
            spatialIndex,
            config.maxDistanceToStop
          );

          if (originStops.length === 0 && destStops.length === 0) {
            rejectionBreakdown.noStopsAtEitherEnd++;
            continue;
          }
          if (originStops.length === 0) {
            rejectionBreakdown.noOriginStops++;
            continue;
          }
          if (destStops.length === 0) {
            rejectionBreakdown.noDestStops++;
            continue;
          }

          const eligible = isRideEligibleForFixedRoute(
            ride,
            spatialIndex,
            selectedRoutes,
            frodoConfig,
            filteredStopsMap
          );

          if (!eligible) {
            rejectionBreakdown.noEligibleRouteOrTime++;
          }
        }

        const processingTime = Date.now() - startTime;

        results.push({
          testId: config.id,
          testName: config.name,
          report,
          offloadedRides,
          remainingRides,
          processingTime,
          selectedRouteIds: [...config.selectedRouteIds],
          maxDistanceToStop: config.maxDistanceToStop,
          rejectionBreakdown,
        });
      }

      setTestResults(results);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('frodoTest.errors.unknownError');
      setError(t('frodoTest.errors.runTestsFailed', { message: errorMessage }));
      console.error('Error running FRODO tests:', err);
    } finally {
      setIsRunning(false);
    }
  };

  // Prepare time series data for chart
  const timeSeriesData = useMemo(() => {
    if (testResults.length === 0) return null;

    // Get all unique hours from all tests
    const allHours = new Set<string>();
    testResults.forEach((result) => {
      const offloadedBins = binRidesPerHour(result.offloadedRides);
      const remainingBins = binRidesPerHour(result.remainingRides);
      offloadedBins.forEach((b) => allHours.add(b.hour));
      remainingBins.forEach((b) => allHours.add(b.hour));
    });
    const sortedHours = Array.from(allHours).sort();
    
    // Store full hour labels for tooltips (outside chart data)
    const fullHourLabels = sortedHours;

    // Generate datasets for each test
    const datasets: any[] = [];
    const colors = [
      { offloaded: 'rgba(25, 118, 210, 1)', remaining: 'rgba(25, 118, 210, 0.3)' },
      { offloaded: 'rgba(255, 152, 0, 1)', remaining: 'rgba(255, 152, 0, 0.3)' },
      { offloaded: 'rgba(76, 175, 80, 1)', remaining: 'rgba(76, 175, 80, 0.3)' },
      { offloaded: 'rgba(156, 39, 176, 1)', remaining: 'rgba(156, 39, 176, 0.3)' },
      { offloaded: 'rgba(244, 67, 54, 1)', remaining: 'rgba(244, 67, 54, 0.3)' },
      { offloaded: 'rgba(0, 188, 212, 1)', remaining: 'rgba(0, 188, 212, 0.3)' },
      { offloaded: 'rgba(255, 87, 34, 1)', remaining: 'rgba(255, 87, 34, 0.3)' },
      { offloaded: 'rgba(121, 85, 72, 1)', remaining: 'rgba(121, 85, 72, 0.3)' },
      { offloaded: 'rgba(63, 81, 181, 1)', remaining: 'rgba(63, 81, 181, 0.3)' },
      { offloaded: 'rgba(233, 30, 99, 1)', remaining: 'rgba(233, 30, 99, 0.3)' },
    ];

    testResults.forEach((result, index) => {
      const color = colors[index % colors.length];
      const offloadedBins = binRidesPerHour(result.offloadedRides);
      const remainingBins = binRidesPerHour(result.remainingRides);

      // Create maps for quick lookup
      const offloadedMap = new Map(offloadedBins.map((b) => [b.hour, b.count]));
      const remainingMap = new Map(remainingBins.map((b) => [b.hour, b.count]));

      // Create data arrays aligned to sorted hours
      const offloadedData = sortedHours.map((hour) => offloadedMap.get(hour) || 0);
      const remainingData = sortedHours.map((hour) => remainingMap.get(hour) || 0);

      // Add offloaded series
      datasets.push({
        label: `${result.testName} - ${t('frodoTest.results.headers.ridesOffloaded')}`,
        data: offloadedData,
        borderColor: color.offloaded,
        backgroundColor: color.offloaded,
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointHoverRadius: 5,
      });

      // Add remaining series
      datasets.push({
        label: `${result.testName} - ${t('frodoTest.results.headers.ridesRemaining')}`,
        data: remainingData,
        borderColor: color.remaining,
        backgroundColor: color.remaining,
        borderWidth: 1,
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 2,
        pointHoverRadius: 4,
      });
    });

    // Format hour labels (show just time if single day, or date+time if multiple days)
    const formatHourLabel = (hourStr: string): string => {
      const parts = hourStr.split(' ');
      if (parts.length === 2) {
        const [date, time] = parts;
        // Check if all hours are from the same date
        const allSameDate = sortedHours.every((h) => h.startsWith(date));
        return allSameDate ? time : hourStr;
      }
      return hourStr;
    };

    return {
      chartData: {
        labels: sortedHours.map(formatHourLabel),
        datasets,
      },
      fullHourLabels: sortedHours, // Store full labels for tooltips
    };
  }, [testResults]);

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('frodoTest.title')}
        </Typography>
        <Tooltip
          title={t('frodoTest.titleHelp')}
          arrow
        >
          <Icon name="circle-info" size={20} style={{ cursor: 'help' }} />
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('frodoTest.description')}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Layer Selection */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('frodoTest.selectTripLayer')}
          </Typography>
          <Tooltip
            title={t('frodoTest.selectTripLayerHelp')}
            arrow
          >
            <Icon name="circle-info" size={20} style={{ cursor: 'help' }} />
          </Tooltip>
        </Box>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('frodoTest.selectTripLayer')}</InputLabel>
          <Select
            value={selectedLayerId || ''}
            onChange={(e) => setSelectedLayerId(e.target.value as number)}
            label={t('frodoTest.selectTripLayer')}
          >
            {availableLayers.map((layer) => (
              <MenuItem key={layer.id} value={layer.id}>
                {t('frodoTest.layerOption', { name: layer.name, total: layer.tripCount.toLocaleString() })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {availableLayers.length === 0 && (
          <Alert severity="info">
            {t('frodoTest.noLayersAvailable')}
          </Alert>
        )}
      </Box>

      {/* Test Configuration Table */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('frodoTest.testConfigurations')}
            </Typography>
            <Tooltip
              title={t('frodoTest.testConfigurationsHelp')}
              arrow
            >
              <Icon name="circle-info" size={20} style={{ cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Icon size={20} name="plus" />}
            onClick={() => setCustomTestDialogOpen(true)}
            size="small"
          >
            {t('frodoTest.addTest')}
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                  <TableCell>
                    <Tooltip title={t('frodoTest.table.testNameHelp')} arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {t('frodoTest.table.testName')}
                        <Icon name="circle-info" size={20} style={{ cursor: 'help' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('frodoTest.table.maxDistanceHelp')} arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                        {t('frodoTest.table.maxDistance')}
                        <Icon name="circle-info" size={20} style={{ cursor: 'help' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('frodoTest.table.maxWaitTimeHelp')} arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                        {t('frodoTest.table.maxWaitTime')}
                        <Icon name="circle-info" size={20} style={{ cursor: 'help' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('frodoTest.table.excludeWheelchairHelp')} arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                        {t('frodoTest.table.excludeWheelchair')}
                        <Icon name="circle-info" size={20} style={{ cursor: 'help' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('frodoTest.table.allowTransfersHelp')} arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                        {t('frodoTest.table.allowTransfers')}
                        <Icon name="circle-info" size={20} style={{ cursor: 'help' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t('frodoTest.table.routesSelectedHelp')} arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {t('frodoTest.table.routesSelected')}
                        <Icon name="circle-info" size={20} style={{ cursor: 'help' }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">{t('frodoTest.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testConfigurations.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>
                    {config.name}
                    {config.isPreset && (
                      <Chip label={t('frodoTest.table.preset')} size="small" color="primary" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">{config.maxDistanceToStop}</TableCell>
                  <TableCell align="right">{config.maxWaitTimeMinutes}</TableCell>
                  <TableCell align="center">{config.excludeWheelchairUsers ? t('common.yes') : t('common.no')}</TableCell>
                  <TableCell align="center">{config.allowTransfers ? t('common.yes') : t('common.no')}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenRouteSelection(config.id)}
                    >
                      {config.selectedRouteIds.length === routes.length
                        ? t('frodoTest.table.allRoutes')
                        : t('frodoTest.table.routesCount', { count: config.selectedRouteIds.length })}
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    {!config.isPreset && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveTest(config.id)}
                      >
                        <Icon name="trash" size={20} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Run Tests Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Tooltip
          title={
            !selectedLayerId
              ? t('frodoTest.runTestsTooltip.noLayer')
              : testConfigurations.length === 0
              ? t('frodoTest.runTestsTooltip.noConfigs')
              : t('frodoTest.runTestsTooltip.ready')
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              size="large"
              startIcon={isRunning ? <CircularProgress size={20} /> : <Icon name="play" />}
              onClick={handleRunTests}
              disabled={isRunning || !selectedLayerId || testConfigurations.length === 0}
            >
              {isRunning ? t('frodoTest.runningTests') : t('frodoTest.runTests')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results Section */}
      {testResults.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('frodoTest.results.title')}
            </Typography>
            {pinnedResult && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('frodoTest.results.pinnedPreview', { name: pinnedResult.testName })}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setPinnedResultId(null)}
                >
                  {t('frodoTest.results.clearPin')}
                </Button>
              </Box>
            )}
            <Tooltip title={t('frodoTest.results.exportCsvTooltip')} arrow>
              <Button
                variant="outlined"
                startIcon={<Icon size={20} name="file-arrow-down" />}
                onClick={handleExportComparisonCSV}
                size="small"
              >
                {t('frodoTest.results.exportCsv')}
              </Button>
            </Tooltip>
          </Box>

          {/* Time Series Chart */}
          {timeSeriesData && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                {t('frodoTest.results.rideRequestsOverTime')}
              </Typography>
              <Box sx={{ height: 400 }}>
                <Line
                  data={timeSeriesData.chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                    plugins: {
                      legend: {
                        display: true,
                        position: 'right' as const,
                      },
                      tooltip: {
                        callbacks: {
                          title: (tooltipItems: any[]) => {
                            if (tooltipItems.length > 0 && timeSeriesData && timeSeriesData.fullHourLabels) {
                              const index = tooltipItems[0].dataIndex;
                              return timeSeriesData.fullHourLabels[index] || timeSeriesData.chartData.labels[index] || '';
                            }
                            return '';
                          },
                          label: (context: any) => {
                            const label = context.dataset.label || '';
                            const value = context.parsed.y;
                            return t('frodoTest.results.rides', { count: value, label, value });
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: t('frodoTest.results.numberOfRides'),
                        },
                        ticks: {
                          stepSize: 1,
                        },
                      },
                      x: {
                        title: {
                          display: true,
                          text: t('frodoTest.results.time'),
                        },
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45,
                        },
                      },
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('frodoTest.results.ridesNote')}
              </Typography>
            </Paper>
          )}

          {/* Comparison Table */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('frodoTest.results.headers.testName')}</TableCell>
                  <TableCell align="right">{t('frodoTest.results.headers.totalRides')}</TableCell>
                  <TableCell align="right">{t('frodoTest.results.headers.ridesOffloaded')}</TableCell>
                  <TableCell align="right">{t('frodoTest.results.headers.ridesRemaining')}</TableCell>
                  <TableCell align="right">{t('frodoTest.results.headers.offloadPercent')}</TableCell>
                  <TableCell align="right">{t('frodoTest.results.headers.avgWaitTime')}</TableCell>
                  <TableCell align="right">{t('frodoTest.results.headers.avgOriginDistance')}</TableCell>
                  <TableCell align="right">{t('frodoTest.results.headers.avgDestDistance')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testResults.map((result) => (
                  <TableRow
                    key={result.testId}
                    hover
                    onMouseEnter={() => setHoveredResultId(result.testId)}
                    onMouseLeave={() => setHoveredResultId((prev) => (prev === result.testId ? null : prev))}
                    onClick={() =>
                      setPinnedResultId((prev) => (prev === result.testId ? null : result.testId))
                    }
                    sx={{
                      cursor: 'pointer',
                      backgroundColor:
                        pinnedResultId === result.testId ? 'action.selected' : undefined,
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell>{result.testName}</TableCell>
                    <TableCell align="right">
                      {result.report.totalOffloaded + result.report.totalRemaining}
                    </TableCell>
                    <TableCell align="right">{result.report.totalOffloaded}</TableCell>
                    <TableCell align="right">{result.report.totalRemaining}</TableCell>
                    <TableCell align="right">{result.report.offloadPercentage.toFixed(1)}%</TableCell>
                    <TableCell align="right">
                      {result.report.routeStats.length > 0
                        ? (
                            result.report.routeStats.reduce((sum, stat) => sum + stat.avgWaitTime, 0) /
                            result.report.routeStats.length
                          ).toFixed(1)
                        : t('common.notAvailable')}
                    </TableCell>
                    <TableCell align="right">
                      {result.report.routeStats.length > 0
                        ? Math.round(
                            result.report.routeStats.reduce((sum, stat) => sum + stat.avgOriginDistance, 0) /
                              result.report.routeStats.length
                          )
                        : t('common.notAvailable')}
                    </TableCell>
                    <TableCell align="right">
                      {result.report.routeStats.length > 0
                        ? Math.round(
                            result.report.routeStats.reduce((sum, stat) => sum + stat.avgDestDistance, 0) /
                              result.report.routeStats.length
                          )
                        : t('common.notAvailable')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Detailed Reports */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {t('frodoTest.results.detailedReports')}
            </Typography>
            {testResults.map((result) => (
              <Accordion key={result.testId} sx={{ mb: 1 }}>
                <AccordionSummary
                  expandIcon={<Icon size={20} name="chevron-down" />}
                  onMouseEnter={() => setHoveredResultId(result.testId)}
                  onMouseLeave={() => setHoveredResultId((prev) => (prev === result.testId ? null : prev))}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t('frodoTest.results.offloadedSummary', { name: result.testName, percent: result.report.offloadPercentage.toFixed(1) })}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {(() => {
                    const breakdown = result.rejectionBreakdown ?? {
                      excludedWheelchair: 0,
                      noOriginStops: 0,
                      noDestStops: 0,
                      noStopsAtEitherEnd: 0,
                      noEligibleRouteOrTime: 0,
                    };
                    return (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {t('frodoTest.results.rejectionBreakdown')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {t('frodoTest.results.rejectionDiagnostic')}
                    </Typography>
                    <Typography variant="body2">
                      {t('frodoTest.results.rejectionDetail', {
                        noStopsAtEitherEnd: breakdown.noStopsAtEitherEnd,
                        noOriginStops: breakdown.noOriginStops,
                        noDestStops: breakdown.noDestStops,
                        noEligibleRouteOrTime: breakdown.noEligibleRouteOrTime,
                        excludedWheelchair: breakdown.excludedWheelchair,
                      })}
                    </Typography>
                  </Box>
                    );
                  })()}
                  <FrodoReportComponent
                    report={result.report}
                    offloadedRides={result.offloadedRides}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      )}

      {/* Add Custom Test Dialog */}
      <Dialog
        open={customTestDialogOpen}
        onClose={() => {
          setCustomTestDialogOpen(false);
          setCustomTestFieldErrors({});
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('frodoTest.customDialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label={t('frodoTest.customDialog.testName')}
              value={newTestName}
              onChange={(e) => {
                setNewTestName(e.target.value);
                if (customTestFieldErrors.name) {
                  setCustomTestFieldErrors((prev) => ({ ...prev, name: undefined }));
                }
              }}
              fullWidth
              required
              error={Boolean(customTestFieldErrors.name)}
              helperText={customTestFieldErrors.name || t('frodoTest.customDialog.testNameHelper')}
              autoFocus
            />
            <TextField
              label={t('frodoTest.customDialog.maxDistance')}
              type="number"
              value={newTestMaxDistance}
              onChange={(e) => {
                setNewTestMaxDistance(parseFloat(e.target.value) || 0);
                if (customTestFieldErrors.maxDistance) {
                  setCustomTestFieldErrors((prev) => ({ ...prev, maxDistance: undefined }));
                }
              }}
              fullWidth
              required
              inputProps={{ min: 1 }}
              error={Boolean(customTestFieldErrors.maxDistance)}
              helperText={customTestFieldErrors.maxDistance || t('frodoTest.customDialog.maxDistanceHelper')}
            />
            <TextField
              label={t('frodoTest.customDialog.maxWait')}
              type="number"
              value={newTestMaxWaitTime}
              onChange={(e) => {
                setNewTestMaxWaitTime(parseFloat(e.target.value) || 0);
                if (customTestFieldErrors.maxWait) {
                  setCustomTestFieldErrors((prev) => ({ ...prev, maxWait: undefined }));
                }
              }}
              fullWidth
              required
              inputProps={{ min: 1 }}
              error={Boolean(customTestFieldErrors.maxWait)}
              helperText={customTestFieldErrors.maxWait || t('frodoTest.customDialog.maxWaitHelper')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newTestExcludeWheelchair}
                  onChange={(e) => setNewTestExcludeWheelchair(e.target.checked)}
                />
              }
              label={t('frodoTest.customDialog.excludeWheelchair')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={newTestAllowTransfers}
                  onChange={(e) => setNewTestAllowTransfers(e.target.checked)}
                />
              }
              label={t('frodoTest.customDialog.allowTransfers')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomTestDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleAddCustomTest} variant="contained">
            {t('frodoTest.addTest')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Route Selection Modal */}
      <FrodoRouteSelectionModal
        open={routeSelectionModalOpen}
        onClose={() => {
          setRouteSelectionModalOpen(false);
          setRouteSelectionTestId(null);
        }}
        selectedRouteIds={
          routeSelectionTestId
            ? testConfigurations.find((c) => c.id === routeSelectionTestId)?.selectedRouteIds || []
            : []
        }
        onSelectionChange={handleRouteSelectionChange}
      />
    </Box>
  );
}

